import { Button, Flex, Modal } from 'antd';
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { useCreateSecretLoginQuery } from '@app/api/User/api';
import { CloseIconsForModalWithDataTestId } from '@app/components';
import { ERROR, FRONT_WARNING, SUCCESS } from '@app/constants';
import { NotificationContext } from '@app/providers';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type { ErrorResponse } from '@app/types/Errors';

import type { GetSecretParams } from '@pages/Organizations/OrganizationsList/types';

export type SecretLinkModalApi = {
  open: (data: GetSecretParams) => void;
};

export const SecretLinkModal = forwardRef<SecretLinkModalApi>((_, ref) => {
  const { showNotification } = useContext(NotificationContext);
  const [visible, toggleVisibility] = useState(false);
  const [params, setParams] = useState<GetSecretParams>();
  const { data, error, isError } = useCreateSecretLoginQuery(
    {
      databaseId: params?.databaseId,
      forUserId: params?.forUserId,
      ActiveBranchId: 1,
    },
    {
      skip: !params?.databaseId || !params?.forUserId,
    }
  );

  useEffect(() => {
    if (error) {
      const errorMessage = (error as ErrorResponse).data;

      showNotification(errorMessage, ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useImperativeHandle(ref, () => ({
    open: (data) => {
      toggleVisibility(true);
      setParams(data);
    },
  }));

  const secretLink = useMemo(() => {
    const host = window.location.origin;

    return `${host}/authorization/?secret=${data?.Secret}`;
  }, [data]);

  const closeHandler = () => {
    toggleVisibility(false);
  };

  const copyHandler = async () => {
    try {
      await navigator.clipboard.writeText(secretLink);
      showNotification('Ссылка скопирована в буфер обмена.', SUCCESS);
    } catch (error) {
      showNotification('Ошибка при копировании в буфер обмена!', FRONT_WARNING);
    }
  };

  if (isError) {
    return null;
  }

  return (
    <Modal
      centered
      open={visible}
      onCancel={closeHandler}
      title={generateSpanWithDataTestId('Ссылка для входа', 'login-link-title')}
      data-testid="login-link-popup"
      closeIcon={<CloseIconsForModalWithDataTestId />}
      footer={
        <Flex gap={10} justify="end">
          <Button
            type="primary"
            onClick={copyHandler}
            data-testid="copy-login-link"
          >
            Скопировать
          </Button>
          <Button type="default" onClick={closeHandler} data-testid="close">
            Закрыть
          </Button>
        </Flex>
      }
    >
      <div>
        <p style={{ color: 'blue' }} data-testid="login-link">
          {secretLink}
        </p>
        <p data-testid="login-description">
          Для авторизации по данной ссылке, нужно открыть браузер в режиме
          инкогнито.
        </p>
      </div>
    </Modal>
  );
});
